import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import ClaimsProgressRail from './ClaimsProgressRail'
import { deriveClaimFlowSteps, type ClaimFlowSnapshot } from '../claims/claimFlowModel'

export type ClaimsDesktopProgressProps = Omit<ClaimFlowSnapshot, 'pathname'>

/**
 * Derives completed-step history from the current route plus snapshot fields and renders the desktop rail.
 * A step appears only after the user has left that screen; snapshot fields should match the page draft.
 */
export default function ClaimsDesktopProgress(props: ClaimsDesktopProgressProps) {
  const { pathname } = useLocation()
  const {
    policySummary = null,
    towingAnswer = null,
    windscreenDescription = null,
    windscreenSmallChip = null,
    windscreenReplacedAlready = null,
    windscreenRepairOption = null,
    windscreenGstRegistered = null,
    windscreenAbn = null,
    windscreenItcEntitled = null,
    windscreenItcPercentage = null,
    claimEntryBranch = null,
    incidentSummary = null,
    otherClaimSubtypeId = null,
    multiVehicleCollisionSummary = null,
    multiVehicleOtherPartySummary = null,
    multiVehicleIncidentDescription = null,
    multiVehicleDamageDescription = null,
    multiVehicleClaimDescription = null,
    weatherClaimConditionsDescription = null,
    fireClaimTypeDescription = null,
    theftOrBreakInDetailDescription = null,
    singleVehicleCollisionSummary = null,
    singleVehicleIncidentDescription = null,
    singleVehicleDamageDescription = null,
    weatherVehicleParkedAtIncident = null,
    fireVehicleParkedAtIncident = null,
    theftVehicleParkedAtIncident = null,
  } = props

  const steps = useMemo(
    () =>
      deriveClaimFlowSteps({
        pathname,
        policySummary,
        towingAnswer,
        windscreenDescription,
        windscreenSmallChip,
        windscreenReplacedAlready,
        windscreenRepairOption,
        windscreenGstRegistered,
        windscreenAbn,
        windscreenItcEntitled,
        windscreenItcPercentage,
        claimEntryBranch,
        incidentSummary,
        otherClaimSubtypeId,
        multiVehicleCollisionSummary,
        multiVehicleOtherPartySummary,
        multiVehicleIncidentDescription,
        multiVehicleDamageDescription,
        multiVehicleClaimDescription,
        weatherClaimConditionsDescription,
        fireClaimTypeDescription,
        theftOrBreakInDetailDescription,
        singleVehicleCollisionSummary,
        singleVehicleIncidentDescription,
        singleVehicleDamageDescription,
        weatherVehicleParkedAtIncident,
        fireVehicleParkedAtIncident,
        theftVehicleParkedAtIncident,
      }),
    [
      pathname,
      policySummary,
      towingAnswer,
      windscreenDescription,
      windscreenSmallChip,
      windscreenReplacedAlready,
      windscreenRepairOption,
      windscreenGstRegistered,
      windscreenAbn,
      windscreenItcEntitled,
      windscreenItcPercentage,
      claimEntryBranch,
      incidentSummary,
      otherClaimSubtypeId,
      multiVehicleCollisionSummary,
      multiVehicleOtherPartySummary,
      multiVehicleIncidentDescription,
      multiVehicleDamageDescription,
      multiVehicleClaimDescription,
      weatherClaimConditionsDescription,
      fireClaimTypeDescription,
      theftOrBreakInDetailDescription,
      singleVehicleCollisionSummary,
      singleVehicleIncidentDescription,
      singleVehicleDamageDescription,
      weatherVehicleParkedAtIncident,
      fireVehicleParkedAtIncident,
      theftVehicleParkedAtIncident,
    ]
  )

  return <ClaimsProgressRail steps={steps} />
}
